/**
 * Notification data access.
 *
 * The unread count polls; nothing else does. A badge that only updates on
 * navigation is a badge people stop trusting, and one WebSocket for one integer
 * is not a trade worth making yet.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/shared/api/client";
import type { Page } from "@/shared/api/types";
import { useAuth } from "@/shared/auth/hooks";

import type {
  Channel,
  DeliveryRow,
  InboxNotification,
  NotificationCategory,
  NotificationPreference,
} from "./types";

function useTenantId(): string | undefined {
  return useAuth().session?.tenant.id;
}

export const notificationKeys = {
  all: (tenant?: string) => ["tenant", tenant, "notifications"] as const,
  inbox: (tenant: string | undefined, unreadOnly: boolean) =>
    ["tenant", tenant, "notifications", "inbox", unreadOnly] as const,
  unread: (tenant?: string) => ["tenant", tenant, "notifications", "unread"] as const,
  preferences: (tenant?: string) => ["tenant", tenant, "notifications", "preferences"] as const,
  deliveries: (tenant: string | undefined, filters: Record<string, unknown>) =>
    ["tenant", tenant, "notifications", "deliveries", filters] as const,
};

export function useUnreadCount() {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: notificationKeys.unread(tenantId),
    queryFn: () => api.get<{ unread: number }>("/notifications/unread-count"),
    enabled: Boolean(tenantId),
    // Cheap: one indexed count. Frequent enough that a message sent while the
    // user is looking at the page appears without a reload.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    // A failing badge must never surface an error banner over the whole shell.
    retry: false,
  });
}

export function useInbox(unreadOnly = false) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: notificationKeys.inbox(tenantId, unreadOnly),
    queryFn: () =>
      api.get<Page<InboxNotification>>(
        `/notifications?include_total=true&limit=50${unreadOnly ? "&unread_only=true" : ""}`,
      ),
    enabled: Boolean(tenantId),
    placeholderData: keepPreviousData,
  });
}

export function useMarkRead() {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: (notificationIds?: string[]) =>
      api.post<{ marked: number }>("/notifications/read", {
        notification_ids: notificationIds ?? null,
      }),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all(tenantId) }),
  });
}

export function usePreferences() {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: notificationKeys.preferences(tenantId),
    queryFn: () => api.get<NotificationPreference[]>("/notifications/preferences"),
    enabled: Boolean(tenantId),
  });
}

export function useUpdatePreference() {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: (payload: {
      category: NotificationCategory;
      channel: Channel;
      is_enabled: boolean;
    }) => api.put<NotificationPreference>("/notifications/preferences", payload),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: notificationKeys.preferences(tenantId) }),
  });
}

export function useDeliveryLog(filters: { status?: string; channel?: string }) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: notificationKeys.deliveries(tenantId, filters),
    queryFn: () => {
      const params = new URLSearchParams({ include_total: "true", limit: "50" });
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.set(key, value);
      });
      return api.get<Page<DeliveryRow>>(`/notifications/deliveries?${params}`);
    },
    enabled: Boolean(tenantId),
    placeholderData: keepPreviousData,
    // Delivery is in flight while an admin watches this screen.
    refetchInterval: 15_000,
  });
}
